/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IntlTelInput from 'react-intl-tel-input';
import PropTypes from 'prop-types';

import { doUpdatePhoneNumberData, setUserInputOnCtrl } from '../../../form/reducer/form';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { ctrlDefaults } from '../../../../shared/form-lib';
import { selectCtrlIdLabelMap } from '../../selectors';
import { controlValidate } from '../../../form/utils';
import { ControlPropType } from '../../ctrl_proptype';
import 'react-intl-tel-input/dist/main.css';
import {
  ValidationWarning,
  MainfieldWrapper,
  FieldWrapper,
  SubtextWrapper,
  LabelWrapper,
  InputWrapper,
  Field,
} from '../../common-field-styles';

const phoneUtil = PhoneNumberUtil.getInstance();
const getNationalFormat = (value) => {
  try {
    return phoneUtil.format(phoneUtil.parse(value), PhoneNumberFormat.NATIONAL);
  } catch (error) {
    // console.error(error);
    return null;
  }
};

const PhoneNumber = ({ ctrl, inTable, formDesign }) => {
  const dispatch = useDispatch();
  const intelTelRef = useRef(null);
  const {
    subtext,
    readonly,
    required,
    placeholder,
    disallow_dropdown,
    phone_valid_codes,
    phone_codes_limit,
    phone_default_code,
    preferred_phone_codes,
  } = ctrl.props;
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const responseStore = form?.response_store ?? 'formplus';
  const { inBuilder, inCustomise, inForm, currentPageNo } = useContext(FormContext);
  const { inPreviewPage: inBuilderPreview } = useSelector((state) => state.builder.present);

  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const [dialCode, setDialCode] = useState('');
  const [newCtrl, setNewCtrl] = useState(ctrl);
  const [inputValue, setInputValue] = useState(ctrl.value ?? '');
  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError ?? '');

  // Reset country list by phone_valid_codes if set
  useEffect(() => {
    if ((inBuilderPreview || inForm) && phone_codes_limit && phone_valid_codes.length && intelTelRef) {
      const allCountries = intelTelRef.current.countries;
      const phoneDefaultCode = phone_default_code.toString().toLowerCase();
      const onlyCountries = allCountries.filter((c) => phone_valid_codes.includes(c.iso2));
      const preferredCountries = allCountries.filter((c) => c.iso2 === phoneDefaultCode);
      intelTelRef.current.countries = onlyCountries;
      intelTelRef.current.preferredCountries = phone_default_code ? preferredCountries : [];
    }
  }, [inForm, inBuilderPreview]);

  // Useful on saved/edited/autofilled forms
  useEffect(() => {
    // The + sign triggers the IntlTelInput component
    // to set appropriate country code for phone number value
    setInputValue(ctrl.value ? `+${ctrl.value}` : '');

    if (ctrl.value === inputValue) return;

    const restoreNational = setTimeout(() => {
      let nationalFormat;
      nationalFormat = getNationalFormat(`+${ctrl.value}`);
      if (nationalFormat) setInputValue(nationalFormat);
    });

    return () => clearTimeout(restoreNational);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctrl.value]);

  // Formats phone number value when dial code changes
  useEffect(() => {
    let nationalFormat;
    if (dialCode) {
      const plusSignAndDialCode = `+${dialCode}`;
      const dialCodeOnly = dialCode;

      if (inputValue.startsWith(plusSignAndDialCode)) {
        nationalFormat = getNationalFormat(inputValue);
      }

      if (inputValue.startsWith(dialCodeOnly)) {
        nationalFormat = getNationalFormat(`+${inputValue}`);
      }

      if (nationalFormat) setInputValue(nationalFormat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialCode]);

  useEffect(() => {
    setCtrlValidationError(ctrl._validationError);
  }, [ctrl._validationError]);

  const saveUpdatedCtrl = (newValue, isValid, property) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property,
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid,
      })
    );
  };

  // NOTE: Ignore eslint indication to remove unused params from `onPhoneNumberChange && onPhoneNumberBlur`
  const onPhoneNumberChange = (status, phoneNumber, country) => {
    if (!inForm) return;

    setInputValue(phoneNumber);
    setNewCtrl((prevCtrl) => ({ ...prevCtrl, value: phoneNumber }));
    if (dialCode !== country.dialCode) setDialCode(country.dialCode);
  };

  const onPhoneNumberBlur = (status, value, countryData, phoneNumber) => {
    if (!inForm) return;

    const { dialCode, iso2 } = countryData;
    const cleanedPhoneNumber = phoneNumber.replace(/[^\d]/g, '');

    const formattedNumber = value
      ? cleanedPhoneNumber.startsWith(dialCode)
        ? cleanedPhoneNumber
        : `${Number(dialCode)}${Number(cleanedPhoneNumber)}`
      : '';

    const phoneNumberData = {
      phoneNumber: `+${formattedNumber}`,
      regionCode: iso2,
    };

    dispatch(doUpdatePhoneNumberData({ ctrlId: ctrl.internal_id, data: phoneNumberData }));

    const { validationOk } = controlValidate({
      setCtrlValidationError,
      userCustomTexts,
      phoneNumberData,
      ctrl: newCtrl,
      responseStore
    });

    if (formattedNumber && validationOk) {
      const nationalFormat = getNationalFormat(`+${formattedNumber}`);
      if (nationalFormat) setInputValue(nationalFormat);
    }

    saveUpdatedCtrl(formattedNumber, validationOk, 'value');
  };

  return (
    <>
      <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
        <FieldWrapper>
          {ctrlValidationError && (
            <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
          )}

          <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <LabelWrapper
              required={required}
              notInBuilder={!inBuilder}
              className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
              <label dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
              <SpecialPropsIndicators ctrl={ctrl} />
            </LabelWrapper>

            <InputWrapper
              notInBuilder={!inBuilder}
              id={`fpl-${ctrl.internal_id}`}
              className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
              <IntlTelInput
                format
                errorMessage
                ref={intelTelRef}
                autoComplete="on"
                fieldId="fpls_iti"
                value={inputValue}
                required={required} 
                inputClassName="form-control"
                aria-describedby="descriptive text"
                onPhoneNumberBlur={onPhoneNumberBlur}
                onPhoneNumberChange={onPhoneNumberChange}
                preferredCountries={preferred_phone_codes}
                placeholder={placeholder || '000 000 0000'}
                title={readonly ? 'This field cannot be edited' : ''}
                defaultCountry={phone_default_code.toString().toLowerCase()}
                // allowDropdown={!disallow_dropdown || !inCustomise || !inBuilder}
                disabled={readonly || (inBuilder && !inBuilderPreview) || inCustomise}
                containerClassName={`intl-tel-input ${inBuilderPreview ? 'fpls-iti-disabled' : ''}`}
              />

              <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                {subtext}
              </SubtextWrapper>
            </InputWrapper>
          </Field>
        </FieldWrapper>
      </MainfieldWrapper>
    </>
  );
};

PhoneNumber.defaultProps = {
  formDesign: null,
  inTable: false,
};

PhoneNumber.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default PhoneNumber;
